import type { CentrifugeMessage } from '../messageTypes';
import type { FanLevel } from '../publicMessageTypes';

export interface RoomEndMessage {
  data: {
    type: 'RoomEnd';
    message: { message: string };
    created_at?: number;
  };
  offset: number;
}

export interface MemberKickOutMessage {
  data: {
    type: 'MemberKickOut';
    message: {
      type: string;
      type_en: string;
      type_ja: string;
      name: string;
      idx: number;
      id: string;
      hide: boolean;
    };
    created_at?: number;
  };
  offset: number;
}

export interface MediaUpdateMessage {
  data: {
    type: 'MediaUpdate';
    message: { likeCnt: number; bookmarkCnt: number; totalScoreCnt: number };
    created_at?: number;
  };
  offset: number;
}

export interface ModifyRoomMessage {
  data: {
    type: 'ModifyRoom';
    message: {
      title: string;
      isAdult: boolean;
      isPw: boolean;
      watchLimit: number;
      // ind(개인), music(음악), game(게임), sports(스포츠), etc(기타), adt(성인), edu(교육), shop(쇼핑), eat(먹방), talk(토크)
      category: 'ind' | 'music' | 'game' | 'sports' | 'etc' | 'adt' | 'edu' | 'shop' | 'eat' | 'talk';
      onAirType: 'live' | 'rec';
    };
    created_at?: number;
  };
  offset: number;
}

export interface IntendedAlertMessage {
  data: {
    type: 'IntendedAlert';
    message: string;
  };
  offset: number;
}

export interface BookmarkAlertMessage {
  data: {
    type: 'BookmarkAlert';
    message: string;
  };
  offset: number;
}

export interface IntroEffectMessageUnparsed {
  data: {
    type: 'IntroEffect';
    message: string;
    created_at?: number;
  };
  offset: number;
}

export interface IntroEffectMessage {
  data: {
    type: 'IntroEffect';
    message: {
      idx: string;
      id: string;
      nk: string;
      ie: string;
      vie: string;
      lev: FanLevel;
      rk: number;
      fanName: string;
    };
    created_at?: number;
  };
  offset: number;
}

export interface LiveRoomRankMessageUnparsed {
  data: {
    type: 'LiveRoomRank';
    message: string;
    created_at?: number;
  };
  offset: number;
}

export type LiveRoomRankItem = {
  userIdx: string;
  userId: string;
  userNick: string;
  totalCoin: string;
};

export interface LiveRoomRankMessage {
  data: {
    type: 'LiveRoomRank';
    message: {
      message: string;
      opt: LiveRoomRankItem[];
    };
    created_at?: number;
  };
  offset: number;
}

export interface RecommendMessage {
  data: {
    type: 'Recommend';
    message: {
      nick: string;
      id: string;
    };
    created_at?: number;
  };
  seq?: undefined;
  gen?: undefined;
  offset: number;
  key?: string;
}

export interface RecommendMessageUnparsed {
  data: { type: 'Recommend'; message: string; created_at?: number };
  seq?: undefined;
  gen?: undefined;
  offset: number;
}

export interface ChatIntroMessage {
  data: {
    type: 'ChatIntro';
    message: string;
    created_at?: number;
    variant?: 'll'; // RT 전환 후 삭제 예정. RT 방송을 LL로 시청 중 노출하는 라이브+ 전환 안내
  };
  offset: number;
}

export interface ChatServerDisconnectMessage {
  data: {
    type: 'ChatServerDisconnect';
    message: string;
  };
  offset: number;
}

// 서포터 공지 등록(수정)
export interface CreateNoticeMessage {
  channel: string;
  data: {
    type: 'createNotice';
    message: string;
  };
  offset: number;
}

export interface DeleteNoticeMessage {
  channel: string;
  data: {
    type: 'deleteNotice';
    message: string;
  };
  offset: number;
}

export interface FirstSponMessage {
  data: {
    type: 'FirstSpon';
    message: {
      nick: string;
    };
    created_at?: number;
  };
  seq?: undefined;
  gen?: undefined;
  offset: number;
  key?: string;
}

export const isRoomEndMessage = (item: CentrifugeMessage): item is RoomEndMessage => {
  return 'data' in item && item.data.type === 'RoomEnd';
};

export const isMemberKickOutMessage = (item: CentrifugeMessage): item is MemberKickOutMessage => {
  return 'data' in item && item.data.type === 'MemberKickOut';
};

export const isMediaUpdateMessage = (item: CentrifugeMessage): item is MediaUpdateMessage => {
  return 'data' in item && item.data.type === 'MediaUpdate';
};

export const isModifyRoomMessage = (item: CentrifugeMessage): item is ModifyRoomMessage => {
  return 'data' in item && item.data.type === 'ModifyRoom';
};

export const isIntendedAlertMessage = (item: CentrifugeMessage): item is IntendedAlertMessage => {
  return 'data' in item && item.data.type === 'IntendedAlert';
};

export const isBookmarkAlertMessage = (item: CentrifugeMessage): item is BookmarkAlertMessage => {
  return 'data' in item && item.data.type === 'BookmarkAlert';
};

export const isIntroEffectMessage = (item: CentrifugeMessage): item is IntroEffectMessage => {
  return 'data' in item && item.data.type === 'IntroEffect';
};

export const isLiveRoomRankMessageUnparsed = (item: CentrifugeMessage): item is LiveRoomRankMessageUnparsed => {
  return 'data' in item && item.data.type === 'LiveRoomRank';
};

export const isLiveRoomRankMessage = (item: CentrifugeMessage): item is LiveRoomRankMessage => {
  return 'data' in item && item.data.type === 'LiveRoomRank';
};

export const isRecommendMessage = (item: CentrifugeMessage): item is RecommendMessage => {
  return 'data' in item && item.data.type === 'Recommend';
};

export const isChatIntroItem = (item: CentrifugeMessage): item is ChatIntroMessage => {
  return 'data' in item && item.data.type === 'ChatIntro' && typeof item.data.message === 'string';
};

export const isChatServerDisconnectMessage = (item: CentrifugeMessage): item is ChatServerDisconnectMessage => {
  return 'data' in item && item.data.type === 'ChatServerDisconnect' && typeof item.data.message === 'string';
};

export const isCreateNoticeMessage = (item: CentrifugeMessage): item is CreateNoticeMessage => {
  return 'data' in item && item.data.type === 'createNotice';
};

export const isDeleteNoticeMessage = (item: CentrifugeMessage): item is DeleteNoticeMessage => {
  return 'data' in item && item.data.type === 'deleteNotice';
};

export const isFirstSponMessage = (item: CentrifugeMessage): item is FirstSponMessage => {
  return 'data' in item && item.data.type === 'FirstSpon';
};
